import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Chip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useIsMobile } from '../../../../hooks/useResponsive';
import LocationTypeIcon from '../shared/LocationTypeIcon';
import { ListSkeleton } from '../shared/LocationSkeleton';
import LocationEmptyState from '../shared/LocationEmptyState';
import LocationDetailPanel from '../detail/LocationDetailPanel';
import {
  getLocationId,
  getLocationName,
  getLocationTypeId,
  getLocationTypeName,
  computeBreadcrumbPath
} from '../../utils/locationTransformers';
import { DETAIL_PANEL_SX, MOBILE_DRAWER_PAPER_SX } from '../../utils/locationConstants';

const COLUMNS = [
  { id: 'friendlyName', label: 'Name', sortable: true },
  { id: 'locationType', label: 'Type', sortable: true, hideOnMobile: true },
  { id: 'parentName', label: 'Path', sortable: false, hideOnMobile: true },
  { id: 'deviceCount', label: 'Devices', sortable: true, align: 'center', hideOnMobile: true },
  { id: 'createdAt', label: 'Created', sortable: true, hideOnMobile: true }
];

/**
 * Flat List View - Sortable MUI Table on desktop, compact list on mobile
 */
const FlatListView = ({
  flatLocations,
  filteredLocations,
  paginatedLocations,
  totalCount,
  loading,
  error,
  selectedLocation,
  sortBy,
  sortOrder,
  page,
  rowsPerPage,
  onSortChange,
  onChangePage,
  onChangeRowsPerPage,
  onLocationSelect,
  onRetry,
  detailPanelRef,
  detailPanelProps,
  t
}) => {
  const isMobile = useIsMobile();

  if (loading) {
    return <ListSkeleton rows={10} />;
  }

  if (error) {
    return <LocationEmptyState type="error" errorMessage={error} onAction={onRetry} t={t} />;
  }

  if (filteredLocations.length === 0) {
    return <LocationEmptyState type="no_results" t={t} />;
  }

  // --- Mobile: compact touch-friendly list ---
  if (isMobile) {
    return (
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding>
          {paginatedLocations.map((location) => {
            const locId = getLocationId(location);
            const typeId = getLocationTypeId(location);
            const isSelected = selectedLocation && String(getLocationId(selectedLocation)) === String(locId);
            const breadcrumb = computeBreadcrumbPath(flatLocations, locId);
            const parentPath = breadcrumb.slice(0, -1).map(c => c.name).join(' > ');

            return (
              <ListItem key={locId} disablePadding divider>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => onLocationSelect(location)}
                  sx={{ py: 1.5, minHeight: 56 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationTypeIcon typeId={typeId} size={24} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={isSelected ? 600 : 400} noWrap>
                        {getLocationName(location)}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {parentPath || getLocationTypeName(location)}
                      </Typography>
                    }
                  />
                  {(location.deviceCount > 0) && (
                    <Chip label={location.deviceCount} size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage=""
          sx={{ '.MuiTablePagination-selectLabel': { display: 'none' } }}
        />

        {/* Mobile detail drawer */}
        {selectedLocation && (
          <Drawer
            anchor="bottom"
            open={!!selectedLocation}
            onClose={() => onLocationSelect(null)}
            PaperProps={{ sx: MOBILE_DRAWER_PAPER_SX }}
          >
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <IconButton
                onClick={() => onLocationSelect(null)}
                size="small"
                aria-label="Close detail panel"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <LocationDetailPanel
              selectedLocation={selectedLocation}
              {...detailPanelProps}
            />
          </Drawer>
        )}
      </Box>
    );
  }

  // --- Desktop: full table with side panel ---
  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', height: 'calc(100vh - 230px)' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} align={col.align || 'left'}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortOrder : 'asc'}
                        onClick={() => onSortChange(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLocations.map((location) => {
                const locId = getLocationId(location);
                const isSelected = selectedLocation && String(getLocationId(selectedLocation)) === String(locId);
                const typeId = getLocationTypeId(location);
                const breadcrumb = computeBreadcrumbPath(flatLocations, locId);

                return (
                  <TableRow
                    key={locId}
                    hover
                    selected={isSelected}
                    onClick={() => onLocationSelect(location)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLocationSelect(location); } }}
                    tabIndex={0}
                    role="button"
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationTypeIcon typeId={typeId} size={20} />
                        <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                          {getLocationName(location)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLocationTypeName(location)}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: 'divider' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {breadcrumb.slice(0, -1).map((crumb, idx) => (
                          <React.Fragment key={crumb.id}>
                            {idx > 0 && (
                              <Typography variant="caption" color="text.disabled" sx={{ mx: 0.25 }}>
                                &rsaquo;
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {crumb.name}
                            </Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {location.deviceCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {location.createdAt ? new Date(location.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        />
      </Box>

      {/* Desktop detail panel */}
      {selectedLocation && (
        <Box ref={detailPanelRef} sx={DETAIL_PANEL_SX}>
          <LocationDetailPanel
            selectedLocation={selectedLocation}
            {...detailPanelProps}
          />
        </Box>
      )}
    </Box>
  );
};

export default FlatListView;
